// Free-shipping bar state from the customer-portal `freeShippingEligibility` response.
// The amount is the plain cart subtotal: OMS disqualifies the whole order via
// isEligible/explanation when a rule fails — it never excludes items from the sum.
// Below the goal the bar always shows progress; ineligible items in the cart are
// flagged per line item instead (see the [data-fs-item-type] badges).
export function computeFreeShippingState(subtotalCents, config, currencyRate = 1) {
  if (!config || !config.isFreeShippingEnabled) return { status: 'hidden' };
  const goalCents = Math.round(Number(config.minimumAmount || 0) * 100 * currencyRate);
  if (goalCents <= 0) return { status: 'hidden' };
  if (subtotalCents >= goalCents) {
    if (config.isEligible) return { status: 'earned', percent: 100, remainingCents: 0 };
    return { status: 'blocked', percent: 100, remainingCents: 0, explanation: config.explanation || '' };
  }
  return {
    status: 'progress',
    percent: Math.min(100, (subtotalCents * 100) / goalCents),
    remainingCents: goalCents - subtotalCents,
  };
}

// CSV -> Set of trimmed types (exact-case match, mirroring OMS
// `Shopify::FreeShippingEligibility#ineligible_products`).
export function parseIneligibleTypes(csv) {
  return new Set((csv || '').split(',').map((type) => type.trim()).filter(Boolean));
}

// 'Drinks, Alcohol,Perishables' -> 'Drinks, Alcohol and Perishables'
export function formatTypesList(csv) {
  const parts = (csv || '').split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

const ELIGIBILITY_QUERY = `
  query FreeShippingEligibility($lineItems: [ShopifyLineItem!]!) {
    freeShippingEligibility(lineItems: $lineItems) {
      explanation
      isEligible
      isFreeShippingEnabled
      minimumAmount
      maximumGrams
      limitedProductsMax
      limitedProductTypes
      ineligibleProductTypes
    }
  }
`;

if (typeof window !== 'undefined' && !customElements.get('free-shipping-goal')) {
  class FreeShippingGoal extends HTMLElement {
    connectedCallback() {
      this.endpoint = window.location.host.includes('staging')
        ? 'https://my-staging.tokyotreat.com/graphql'
        : 'https://my.tokyotreat.com/graphql';
      this.progress = this.querySelector('progress-bar');
      this.moneyFormat = window.FoxTheme.settings.moneyFormat;
      this.currencyRate = Number((window.Shopify.currency && window.Shopify.currency.rate) || 1);
      this.config = null;
      this._timer = null;
      this._onCartUpdated = (event) => this.refresh(event.detail && event.detail.cart);
      this._onDocumentClick = (event) => {
        const tooltip = this.querySelector('.free-shipping-tooltip');
        if (!tooltip || !tooltip.open) return;
        if (!tooltip.contains(event.target) || event.target.closest('[data-fs-tooltip-close]')) {
          tooltip.open = false;
        }
      };
      this._onKeydown = (event) => {
        if (event.key !== 'Escape') return;
        const tooltip = this.querySelector('.free-shipping-tooltip[open]');
        if (tooltip) tooltip.open = false;
      };
      document.addEventListener('cart:updated', this._onCartUpdated);
      document.addEventListener('click', this._onDocumentClick);
      document.addEventListener('keydown', this._onKeydown);
      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this._onCartUpdated);
      document.removeEventListener('click', this._onDocumentClick);
      document.removeEventListener('keydown', this._onKeydown);
      clearTimeout(this._timer);
    }

    refresh(cart) {
      clearTimeout(this._timer);
      this._timer = setTimeout(() => this._run(cart), 300);
    }

    async _run(cart) {
      try {
        if (!cart || !cart.items) cart = await (await fetch('/cart.js')).json();
        if (!cart.items.length) return this._apply({ status: 'hidden' }, cart);

        const res = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Request-Source': 'JAPAN_HAUL' },
          body: JSON.stringify({
            query: ELIGIBILITY_QUERY,
            variables: {
              lineItems: cart.items.map((item) => ({
                shopifyProductId: String(item.product_id),
                shopifyVariantId: String(item.variant_id),
                quantity: item.quantity,
              })),
            },
          }),
        });
        if (!res.ok) return this._apply({ status: 'hidden' }, cart);
        const json = await res.json();
        if (json.errors || !json.data) return this._apply({ status: 'hidden' }, cart);

        this.config = json.data.freeShippingEligibility;
        this._apply(computeFreeShippingState(cart.items_subtotal_price, this.config, this.currencyRate), cart);
      } catch (error) {
        this._apply({ status: 'hidden' });
      }
    }

    _apply(state, cart) {
      this._markIneligibleItems(state.status === 'hidden' ? null : this.config);
      this.classList.toggle('hidden', state.status === 'hidden');
      if (state.status === 'hidden') return;

      const count = this.querySelector('[data-fs-count]');
      if (count && cart) count.textContent = `${cart.item_count} item${cart.item_count === 1 ? '' : 's'}`;

      this.classList.toggle('free-shipping-goal--done', state.status === 'earned');
      this.classList.toggle('free-shipping-goal--blocked', state.status === 'blocked');

      if (this.progress) {
        this.progress.style.setProperty('--percent', `${state.percent}%`);
        this.progress.dataset.value = String(state.percent);
        this.progress.dataset.max = '100';
      }

      if (state.status === 'progress') {
        const left = this.querySelector('[data-left-to-spend]');
        if (left) left.innerHTML = window.FoxTheme.Currency.formatMoney(state.remainingCents, this.moneyFormat);
      }

      this._fillTooltip();
    }

    _fillTooltip() {
      const config = this.config;
      if (!config) return;

      this._rule('[data-fs-rule-min]', Boolean(config.minimumAmount), {
        '[data-fs-min]': String(config.minimumAmount),
      });
      this._rule('[data-fs-rule-exclude]', Boolean(config.ineligibleProductTypes), {
        '[data-fs-exclude]': formatTypesList(config.ineligibleProductTypes),
      });
      this._rule('[data-fs-rule-limited]', config.limitedProductsMax > 0 && Boolean(config.limitedProductTypes), {
        '[data-fs-limited-max]': String(config.limitedProductsMax),
        '[data-fs-limited-types]': formatTypesList(config.limitedProductTypes),
      });
      this._rule('[data-fs-rule-weight]', config.maximumGrams > 0, {
        '[data-fs-weight]': String((config.maximumGrams || 0) / 1000),
      });
    }

    // Show a "Not eligible for Free Shipping" badge on every line item whose
    // product type is in the ineligible list; hide the rest (config null = hide all).
    _markIneligibleItems(config) {
      const types = config ? parseIneligibleTypes(config.ineligibleProductTypes) : new Set();
      document.querySelectorAll('[data-fs-item-type]').forEach((badge) => {
        badge.classList.toggle('hidden', !types.has((badge.dataset.fsItemType || '').trim()));
      });
    }

    _rule(ruleSelector, show, values) {
      const rule = this.querySelector(ruleSelector);
      if (!rule) return;
      rule.hidden = !show;
      if (!show) return;
      for (const [selector, text] of Object.entries(values)) {
        const target = rule.querySelector(selector);
        if (target) target.textContent = text;
      }
    }
  }

  customElements.define('free-shipping-goal', FreeShippingGoal);
}
